import React, { useEffect, useState, useRef } from 'react';
import { defaultCharacterConfig } from '../../utils/defaultConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import styled from '@xstyled/styled-components';

// #region Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 500px;
  z-index: 1001;
  position: relative;
`;

const ModalHeader = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;

  button {
    background: none;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: #333;
  }
`;

const ModalContent = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 10px;

  label {
    display: block;
    font-weight: bold;
    margin-bottom: 5px;
    margin-top: 10px;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 20px 20px 0px 10px;
`;

const InputField = styled.input`
  width: 100%;
  padding: 8px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
`;

const ReadOnlyField = styled.div`
  font-weight: normal;
  margin: -3px 0px 10px 0px;
`;

const TextAreaField = styled.textarea`
    width: 100%;
    padding: 8px;
    margin-bottom: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    resize: vertical; /* Allow resizing on the vertical axis */
    overflow-y: auto; /* Enable vertical scrolling */
    max-height: 150px; /* Maximum height */
`;

const InlineInputs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;

  label {
    flex: 1;
  }
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-left: 10px; /* Add some space between buttons */
`;

const SaveButton = styled(ModalButton)`
  background-color: #4caf50;
  color: white;
`;

const CancelButton = styled(ModalButton)`
  background-color: #c9c9c9;
  color: rgb(15, 13, 13);
`;

const DeleteButton = styled(ModalButton)`
  background-color: #ff0000;
  color: white;
  margin-right: auto; /* Move delete button to the left */
`;
// #endregion Styles

const EditConfigModal = ({ characterConfig, isEditingMode, onClose, onDelete, onSave }) => {
  const [editedConfig, setEditedConfig] = useState(characterConfig);
  const [showDeleteButton, setShowDeleteButton] = useState(true);

  const systemMessageRef = useRef(null);
  const assistantMessageRef = useRef(null);

  // #region Hooks
  useEffect(() => {
    setShowDeleteButton(isEditingMode(characterConfig.name));
  }, [characterConfig.name, isEditingMode]);

  useEffect(() => {
    adjustTextAreaHeight(systemMessageRef.current);
    adjustTextAreaHeight(assistantMessageRef.current);
  }, [editedConfig.systemMessage, editedConfig.assistantMessage]);

  // #endregion Hooks

  // #region Handlers
  const adjustTextAreaHeight = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 151)}px`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Define a mapping from input names to parsing functions
    const parseValue = {
      temperature: parseFloat,
      max_tokens: parseInt,
      top_p: parseFloat,
    };

    setEditedConfig((prevConfig) => {
      // Check if there is a parsing function for the current input name
      const parser = parseValue[name];

      // Parse the value or use the previous valid value if the parsing fails
      const updatedValue = parser ? parser(value) || prevConfig[name] : value;

      return {
        ...prevConfig,
        [name]: updatedValue,
      };
    });
  };

  const handleSave = () => {
    try {
      const finalConfig = {
        name: editedConfig.name || defaultCharacterConfig.name,
        role: editedConfig.role || defaultCharacterConfig.role,
        systemMessage: editedConfig.systemMessage || defaultCharacterConfig.systemMessage,
        assistantMessage: editedConfig.assistantMessage || defaultCharacterConfig.assistantMessage,
        temperature: editedConfig.temperature !== undefined ? editedConfig.temperature : defaultCharacterConfig.temperature,
        max_tokens: editedConfig.max_tokens || defaultCharacterConfig.max_tokens,
        top_p: editedConfig.top_p !== undefined ? editedConfig.top_p : defaultCharacterConfig.top_p
      };

      onSave(finalConfig);
      onClose();
    } catch (error) {
      //console.error('Error saving configuration:', error);
    }
  };

  const handleDelete = () => {
    onDelete(characterConfig.name);
  };

  // #endregion Handlers

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <span>Edit Configuration</span>
          <button onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </ModalHeader>
        <ModalContent>
          <label>
            Name:
            <InputField type="text" placeholder='Character Name' name="name" value={editedConfig.name} onChange={handleChange} />
          </label>
          <label>
            Role:
            <ReadOnlyField placeholder='role'>{editedConfig.role || defaultCharacterConfig.role}</ReadOnlyField>
          </label>
          <label>
            System Message:
            <TextAreaField
              ref={systemMessageRef}
              placeholder='System Message'
              name="systemMessage"
              value={editedConfig.systemMessage}
              onChange={handleChange}
            />
          </label>
          <label>
            Assistant Message:
            <TextAreaField
              ref={assistantMessageRef}
              placeholder='Assistant Message'
              name="assistantMessage"
              value={editedConfig.assistantMessage}
              onChange={handleChange}
            />
          </label>
          <InlineInputs>
            <label>
              Temperature:
              <InputField
                type="number"
                name="temperature"
                step="0.05"
                min="0"
                max="1"
                placeholder='0.950'
                value={editedConfig.temperature}
                onChange={handleChange}
              />
            </label>
            <label>
              MAX_TOKENS:
              <InputField
                type="number"
                name="max_tokens"
                step="100"
                min="100"
                max="1500"
                placeholder='500'
                value={editedConfig.max_tokens}
                onChange={handleChange}
              />
            </label>
            <label>
              TOP_P:
              <InputField
                type="number"
                name="top_p"
                step="0.05"
                min="0"
                max="1"
                placeholder='0.95'
                value={editedConfig.top_p}
                onChange={handleChange}
              />
            </label>
          </InlineInputs>
        </ModalContent>
        <ModalButtons>
          {showDeleteButton && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </ModalButtons>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default EditConfigModal;
